// src/components/Tools/Results/TyposquattingResults.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { ResultRendererProps } from './types';

type Row = { domain: string; ip: string; ns: string; mx: string; type?: string };

function normalize(results: any[]): Row[] {
  if (!Array.isArray(results)) return [];
  const items = Array.isArray(results[0]?.items) ? results[0].items : results;
  if (!Array.isArray(items)) return [];
  return items
    .map((it: any) => {
      const v = Array.isArray(it?.values) ? it.values : [];
      const [domain, ip, ns, mx] = v;
      return {
        domain: String(domain ?? ''),
        ip: String(ip ?? ''),
        ns: String(ns ?? ''),
        mx: String(mx ?? ''),
        type: String(it?.type ?? ''),
      };
    })
    .filter((r) => r.domain);
}

// estilos para el chip según el tipo
const typeClasses: Record<string, string> = {
  '*original': 'bg-sky-100 text-sky-800 ring-sky-200',
  original: 'bg-sky-100 text-sky-800 ring-sky-200',
  omission: 'bg-amber-100 text-amber-800 ring-amber-200',
  replacement: 'bg-indigo-100 text-indigo-800 ring-indigo-200',
  subdomain: 'bg-emerald-100 text-emerald-800 ring-emerald-200',
  transposition: 'bg-fuchsia-100 text-fuchsia-800 ring-fuchsia-200',
  'vowel-swap': 'bg-violet-100 text-violet-800 ring-violet-200',
  addition: 'bg-cyan-100 text-cyan-800 ring-cyan-200',
  bitsquatting: 'bg-rose-100 text-rose-800 ring-rose-200',
  homoglyph: 'bg-teal-100 text-teal-800 ring-teal-200',
  insertion: 'bg-lime-100 text-lime-800 ring-lime-200',
  plural: 'bg-orange-100 text-orange-800 ring-orange-200',
  repetition: 'bg-blue-100 text-blue-800 ring-blue-200',
  hyphenation: 'bg-blue-100 text-blue-600 ring-blue-200',
};

const typeKeyMap: Record<string, string> = {
  '*original': 'original',
  original: 'original',
  omission: 'omission',
  replacement: 'replacement',
  subdomain: 'subdomain',
  transposition: 'transposition',
  'vowel-swap': 'vowel-swap',
  addition: 'addition',
  bitsquatting: 'bitsquatting',
  homoglyph: 'homoglyph',
  insertion: 'insertion',
  plural: 'plural',
  repetition: 'repetition',
};

const renderValue = (v: string) => {
  if (!v || v === '-' || v === '–') return <span className="text-slate-400">—</span>;
  if (v.startsWith('!')) {
    return (
      <span className="rounded px-1.5 py-0.5 text-[11px] font-medium bg-rose-50 text-rose-700 ring-1 ring-rose-200">
        {v.replace(/^!/, '')}
      </span>
    );
  }
  return <span className="font-mono text-[12px] break-all">{v}</span>;
};

const PAGE_SIZE = 15;

const TyposquattingResults: React.FC<ResultRendererProps> = ({ results }) => {
  const { t } = useTranslation();
  const rows = normalize(results);

  const [page, setPage] = React.useState(1);
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));

  React.useEffect(() => {
    setPage(1);
  }, [results]);

  if (rows.length === 0) {
    return <p className="text-sm text-slate-500">{t('tools.typo.empty')}</p>;
  }

  const start = (page - 1) * PAGE_SIZE;
  const pageRows = rows.slice(start, start + PAGE_SIZE);

  return (
    <>

      <div className="overflow-auto rounded-lg border border-slate-200 shadow-sm">
        <table className="w-full text-sm table-fixed">
          <colgroup>
            <col className="w-[14%]" /> {/* Tipo */}
            <col className="w-[30%]" /> {/* Dominio */}
            <col className="w-[16%]" /> {/* IP */}
            <col className="w-[20%]" /> {/* NS */}
            <col className="w-[20%]" /> {/* MX */}
          </colgroup>

          <thead className="bg-gradient-to-r from-sky-50 to-indigo-50">
            <tr className="text-indigo-700">
              {(['type','domain','ip','ns','mx'] as const).map((key) => (
                <th
                  key={key}
                  className="px-3 py-3 text-center text-[12px] font-semibold uppercase tracking-wide"
                >
                  {t(`tools.typo.columns.${key}`)}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="[&>tr:nth-child(odd)]:bg-slate-50/50 [&>tr:nth-child(even)]:bg-white">
            {pageRows.map((r, i) => {
              const typeKey = typeKeyMap[r.type ?? ''] ?? r.type ?? '';
              const chipClasses =
                typeClasses[r.type ?? ''] ?? 'bg-slate-100 text-slate-700 ring-slate-200';

              return (
                <tr
                  key={`${r.domain}-${start + i}`}
                  className="hover:bg-slate-100/60 transition-colors"
                >
                  <td className="px-3 py-3 text-center">
                    {typeKey ? (
                      <span
                        className={`inline-block rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1 ${chipClasses}`}
                        title={t(`tools.typo.types.${typeKey}`) as string}
                      >
                        {t(`tools.typo.types.${typeKey}`)}
                      </span>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>

                  <td className="px-3 py-3">
                    <span className="font-medium break-all">{r.domain}</span>
                  </td>

                  <td className="px-3 py-3 text-center">{renderValue(r.ip)}</td>
                  <td className="px-3 py-3 text-center">{renderValue(r.ns)}</td>
                  <td className="px-3 py-3 text-center">{renderValue(r.mx)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      <div className="mt-3 flex items-center justify-between gap-3">
        <button
          className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page === 1}
        >
          ← {t('tools.typo.pagination.prev')}
        </button>

        <span className="text-sm text-slate-600">
          {t('tools.typo.pagination.page_x_of_y', { x: page, y: totalPages })}
        </span>

        <button
          className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          disabled={page === totalPages}
        >
          {t('tools.typo.pagination.next')} →
        </button>
      </div>
    </>
  );
};

export default TyposquattingResults;
